<script lang="ts">
  import { navigate } from 'svelte-routing';
  import DataRequestForm from '$lib/components/DataRequestForm.svelte';
</script>

<svelte:head>
  <title>Privacy - outlive.it</title>
</svelte:head>

<section id="main" class="main">
  <header>
    <h2>Privacy Statement</h2>
  </header>

  <p>
    <b>
    This privacy statement describes how personal information is
    used in the game Outlive.
    </b>
  </p>

  <h4>1) Who is behind the game?</h4>

  <p>
    The long game initiative (Laura and Matias). You can contact us
    <a href="/feedback" on:click|preventDefault={() => navigate('/feedback')}>with this feedback form</a>.
  </p>

  <h4>2) Types of personal data we process</h4>
  <p>
    Playing the game online does not store any of your data.
    <a target="_blank" href="https://themarkup.org/blacklight?url=outlive.it" rel="noopener noreferrer">There are no trackers</a>.
    We don't even use cookies.
  </p>
  <p>
    However, if you choose to subscribe to change of rank notifications,
    we store following personal data:
  </p>
  <ul>
    <li>Your email address</li>
    <li>Month and year of birth. Consider lying about it,
        but within reason. You're probably not 180 years old.</li>
  </ul>

  <h4>3) Lawful basis for processing personal data.</h4>
  <p>
    We store and process your data for the following purposes:
  </p>
  <ul>
    <li>Sending you notifications of level-ups in
        the game.</li>
    <li>Sending you vital information about the game, e.g. if
        more historical figures are added to the game or the game is
        stopped for some reason.</li>
    <li>We invite you to take part in research about the game.
        Possible feedback questionnaires related to the game
        are likely to be anonymous.</li>
  </ul>

  <h4>4) Are we transferring or sharing the data with 3rd parties?</h4>
  <p>
    Never.
  </p>
  <p>
    Outlive.it, including data storage, is based on <a href="https://bunny.net/" rel="noopener noreferrer">bunny.net</a>'s services. We use the data center in Frankfurt,
    Germany. Emails are sent via Microsoft's Azure services using data center in Amsterdam, Netherlands.
  </p>

  <h4>5) How long will we keep your data?</h4>
  <p>
    That is a really good question.
  </p>
  <p>
    Basically, we keep your data until you reach the age of 105, beating
    the current record of pope Agatho and winning the game; or until you
    die and your next of kin unsubscribes you; or you unsubscribe
    from receiving notifications.
  </p>
  <p>
    Another possibility is to keep the data until we die or retire or
    stop the game because it is not fun anymore or we run out of
    players. We may send you a notice when we stop the game and
    remove all player data.
  </p>
  <p>
    Yet another possibility is that some service we're using gets deprecated
    and we don't bother migrating the site to another platform.
  </p>

  <h4>6) Your data rights and how they are supported</h4>
  <p>
    You have following rights to your data:
  </p>

  <h5>Right to be informed</h5>
  <p class="nested">
    Organisations must tell individuals what
    data is being collected, how it's being used, how long it will be kept
    and whether it will be shared with any third parties.<br />
    <b>How do we support this:</b> That is what this document is for.
  </p>

  <h5>Right of access</h5>
  <p class="nested">
    Individuals have the right to request a copy
    of the information that an organisation holds on them.<br />
    <b>How do we support this:</b> you can order a copy of the data to
    the same email address you used to register <a href="#q7">using the
    form at the bottom of this page</a>.
  </p>

  <h5>Right of rectification</h5>
  <p class="nested">
    Individuals can correct inaccurate or incomplete data.<br />
    <b>How do we support this:</b> You can unsubscribe using the link in
    each email we send (which deletes your data) and then re-register
    immediately with any amended data. The game continues with the
    updated data.
  </p>

  <h5>Right to be forgotten</h5>
  <p class="nested">
    In certain circumstances, individuals
    can ask organisations to erase any personal data they've stored.<br />
    <b>How do we support this:</b> Use the unsubscribe link in any email
    we send. This will delete your data. If you are certain you have
    subscribed, but cannot find any messages from us, <a href="#q7">use the
    form at the bottom of this page</a> to receive one with an unsubscribe link.
  </p>

  <h5>Right of portability</h5>
  <p class="nested">
    In some circumstances, individuals can
    request that an organisation transfer any data that it holds on them
    to another company.<br />
    <b>How do we support this:</b> You can order a copy of the data in a
    technical, portable format to the same email address you used to
    register <a href="#q7">using the form at the bottom of this page</a>.
  </p>

  <h5>Right to restrict processing</h5>
  <p class="nested">
    In some circumstances, individuals
    can request that an organisation limits its use of personal data.<br />
    <b>How do we support this:</b> The game cannot be played without the
    data so this is basically the same as removing your data from the
    game. Use the unsubscribe link in any emails we send.
  </p>

  <h5>Right to object</h5>
  <p class="nested">
    <b>Right to object:</b> Individuals have the right to challenge certain
    types of processing, such as direct marketing.<br />
    <b>How do we support this:</b> The data is only used for the game, so you
    can always say no to that, but that is the same as removing your data.
  </p>

  <h5>Rights related to automated decision making, including profiling</h5>
  <p class="nested">
    Under most circumstances, individuals have the right to object to having
    decisions made about them by automated processes or profiling.<br />
    <b>How do we support this:</b> The game is using your age to define your
    rank, so we are kind of profiling you and think it is pretty cool, if
    you make it to the top 50. If you think we should not be doing so, use
    the unsubscribe link included in any email we send to remove your data.
  </p>
</section>

<DataRequestForm />

<style>
  #main {
    padding: 4em 4em 2em 4em;
    background-color: #ffffff;
    border-radius: 4px;
  }

  #main > header:first-child {
    text-align: center;
  }

  h4 {
    margin-top: 2em;
    border-bottom: 1px solid #444444;
  }

  h5 {
    margin-bottom: 0.5em;
  }

  .nested {
    border-left: 4px solid #cccccc;
    padding-left: 1em;
  }

  ul {
    list-style: disc;
    margin: 0 0 1em 0;
    padding-left: 1em;
  }

  ul li {
    padding-left: 0.5em;
  }

  @media screen and (max-width: 736px) {
    #main {
      padding: 2em 2em 0.1em 2em;
    }
  }

  @media screen and (max-width: 480px) {
    #main {
      padding: 2em 1em 0.1em 1em;
    }
  }
</style>
